/**
 * Job Types Service
 * Service layer for job type configuration API endpoints
 *
 * Provides CRUD operations for job types and their custom field definitions.
 */

import { apiClient } from './api';
import type {
  JobTypeDto,
  CreateJobTypeRequest,
  UpdateJobTypeRequest,
  CreateJobTypeResponse,
  GetJobTypeResponse,
  ListJobTypesResponse,
  UpdateJobTypeResponse,
  DeleteJobTypeResponse,
  UpdateJobCustomFieldsRequest,
} from '@/types/job-type';
import type { JobResponse } from '@/types/provider-job';

/**
 * Job Types Service
 */
export const jobTypesService = {
  /**
   * List all job types for the provider
   * @param activeOnly - If true, only return active job types
   */
  async listJobTypes(activeOnly?: boolean): Promise<JobTypeDto[]> {
    const queryParams = new URLSearchParams();
    if (activeOnly) {
      queryParams.append('activeOnly', 'true');
    }

    const url = `/provider/job-types${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await apiClient.get<ListJobTypesResponse>(url);
    return response.jobTypes;
  },

  /**
   * Get a single job type by ID
   */
  async getJobType(id: string): Promise<JobTypeDto> {
    const response = await apiClient.get<GetJobTypeResponse>(
      `/provider/job-types/${id}`
    );
    return response.jobType;
  },

  /**
   * Create a new job type
   */
  async createJobType(data: CreateJobTypeRequest): Promise<JobTypeDto> {
    const response = await apiClient.post<CreateJobTypeResponse>(
      '/provider/job-types',
      data
    );
    return response.jobType;
  },

  /**
   * Update an existing job type
   */
  async updateJobType(
    id: string,
    data: UpdateJobTypeRequest
  ): Promise<JobTypeDto> {
    const response = await apiClient.put<UpdateJobTypeResponse>(
      `/provider/job-types/${id}`,
      data
    );
    return response.jobType;
  },

  /**
   * Delete a job type
   */
  async deleteJobType(id: string): Promise<void> {
    await apiClient.delete<DeleteJobTypeResponse>(`/provider/job-types/${id}`);
  },

  /**
   * Update custom field values on a job
   */
  async updateJobCustomFields(
    jobId: string,
    customFields: Record<string, unknown>
  ): Promise<JobResponse> {
    const data: UpdateJobCustomFieldsRequest = { customFields };
    const response = await apiClient.patch<JobResponse>(
      `/provider/jobs/${jobId}/custom-fields`,
      data
    );
    return response;
  },
};
